import { ref, computed } from 'vue'
import store from '@/store'
import {
  OptionSummary,
  BookingSummary,
} from '@/services/BookingService.interface'
import { PrestType, ServiceType } from '@/config'
import { Service } from '@/services/CatalogService.interface'
import BookingServiceMapper from '@/mappers/BookingServiceMapper'
import { isPromoCodeService } from '@/utils/optionClassification'

const FAMILY_ID_TO_CATEGORY = new Map<number, keyof GroupedOptions>([
  [3, 'skipass'], // for some reason it relates to skipasses as well as Assurance annulation Zen
  [75, 'skipass'],
  [14, 'material'],
  [53, 'material'],
  [54, 'material'],
  [55, 'material'],
  [56, 'material'],
  [63, 'material'],
  // 57 = PRICE_ADJUSTMENT_PROMO — handled via isPromoCodeService, not equipment
  [30, 'helmet'],
  [8, 'train'],
  [83, 'train'],
  [78, 'transfert'],
  [82, 'luggage'],
  [81, 'flight'],
])

interface GroupedOptions {
  train: Service[]
  transfert: Service[]
  accommodation: Service[]
  skipass: Service[]
  material: Service[]
  helmet: Service[]
  luggage: Service[]
  option: Service[]
  flight: Service[]
}

/**
 * Ensures that the object contains a valid service
 */
function isCompleteService(service: Partial<Service>): service is Service {
  return service.id !== undefined && service.nameCommercial !== undefined
}

export function useServiceCategorization(booking: BookingSummary) {
  const services = ref<Service[]>([])
  const additionalServices = ref<OptionSummary[]>([])

  const fetchServices = async () => {
    if (booking) {
      additionalServices.value = booking.options.filter(
        (option: OptionSummary) => option.prestTypeId !== PrestType.Accomodation
      )

      let catalogServices: Service[] = []

      if (additionalServices.value.length > 0) {
        const serviceIds = additionalServices.value.map(
          (option: OptionSummary) => option.prestId
        )

        await store.dispatch('catalog/fetchServices', { serviceIds })

        catalogServices = (store.state.catalog?.services || []).filter(
          isCompleteService
        ) as Service[]
      }

      // Map services, ensuring missing ones from booking are included inside `BookingServiceMapper`
      services.value = BookingServiceMapper.mapServices(
        booking,
        additionalServices.value,
        catalogServices
      )
    }
  }

  const groupedOptions = computed<GroupedOptions>(() => {
    const categories: GroupedOptions = {
      train: [],
      transfert: [],
      accommodation: [],
      skipass: [],
      material: [],
      helmet: [],
      luggage: [],
      option: [],
      flight: [],
    }

    services.value.forEach((service: Service) => {
      if (service.serviceType === ServiceType.ACCOMMODATION) {
        categories.accommodation.push(service)
      } else if (isPromoCodeService(service)) {
        categories.option.push(service)
      } else if (service.familyId === 3) {
        // Check the PrestTypeId for options with familyId === 3
        // for some reason it relates to skipasses as well as Assurance annulation Zen
        const relatedOption = additionalServices.value.find(
          (option: OptionSummary) => option.prestId === service.id
        )
        if (
          relatedOption &&
          relatedOption.prestTypeId === PrestType.Insurance
        ) {
          categories.option.push(service) // Treat it as an option if it's insurance
        } else {
          categories.skipass.push(service) // Otherwise, it's skipass
        }
      } else {
        const category = FAMILY_ID_TO_CATEGORY.get(service.familyId!)
        if (category) {
          categories[category].push(service)
        } else {
          categories.option.push(service) // ✅ Missing services are already handled in `mapServices`
        }
      }
    })

    return categories
  })

  return {
    groupedOptions,
    fetchServices,
  }
}
